// components/crew/WeeklyAvailability.js
import React, { useState } from 'react';
import { Clock, Plus, X } from 'lucide-react';
import { useTranslations } from '../../hooks/useTranslations';

export function WeeklyAvailability({ 
  availability = {}, 
  onChange, 
  disabled = false 
}) {
  const t = useTranslations();
  
  const daysOfWeek = [
    { key: 'sunday', label: t.sunday },
    { key: 'monday', label: t.monday },
    { key: 'tuesday', label: t.tuesday },
    { key: 'wednesday', label: t.wednesday },
    { key: 'thursday', label: t.thursday },
    { key: 'friday', label: t.friday },
    { key: 'saturday', label: t.saturday },
  ];

  // Handle day toggle (working/not working)
  const handleDayToggle = (dayKey) => {
    if (disabled) return;
    
    const currentDay = availability[dayKey] || { isWorking: false, timeRanges: [] };
    const newIsWorking = !currentDay.isWorking;
    
    const updatedAvailability = {
      ...availability,
      [dayKey]: {
        isWorking: newIsWorking,
        timeRanges: newIsWorking ? currentDay.timeRanges : []
      }
    };
    
    onChange(updatedAvailability);
  };

  // Add new time range to a day
  const handleAddTimeRange = (dayKey) => {
    if (disabled) return;
    
    const currentDay = availability[dayKey] || { isWorking: false, timeRanges: [] };
    const newTimeRange = { start: '09:00', end: '17:00' };
    
    const updatedAvailability = {
      ...availability,
      [dayKey]: {
        isWorking: true,
        timeRanges: [...currentDay.timeRanges, newTimeRange]
      }
    };
    
    onChange(updatedAvailability);
  };

  // Remove time range from a day
  const handleRemoveTimeRange = (dayKey, rangeIndex) => {
    if (disabled) return;
    
    const currentDay = availability[dayKey] || { isWorking: false, timeRanges: [] };
    const newTimeRanges = currentDay.timeRanges.filter((_, index) => index !== rangeIndex);
    
    const updatedAvailability = {
      ...availability,
      [dayKey]: {
        isWorking: newTimeRanges.length > 0,
        timeRanges: newTimeRanges
      }
    };
    
    onChange(updatedAvailability);
  };

  // Update time range
  const handleTimeRangeChange = (dayKey, rangeIndex, field, value) => {
    if (disabled) return;
    
    const currentDay = availability[dayKey] || { isWorking: false, timeRanges: [] };
    const newTimeRanges = [...currentDay.timeRanges];
    newTimeRanges[rangeIndex] = {
      ...newTimeRanges[rangeIndex],
      [field]: value
    };
    
    const updatedAvailability = {
      ...availability,
      [dayKey]: {
        ...currentDay,
        timeRanges: newTimeRanges
      }
    };
    
    onChange(updatedAvailability);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center space-x-2 text-blue-600 mb-6">
        <Clock className="w-5 h-5" />
        <h3 className="text-lg font-medium">{t.weeklyAvailability}</h3>
      </div>

      {/* Days */}
      <div className="space-y-6">
        {daysOfWeek.map((day) => {
          const dayData = availability[day.key] || { isWorking: false, timeRanges: [] };
          const isWorking = dayData.isWorking;
          
          return (
            <div key={day.key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              {/* Day header with checkbox */}
              <div className="flex items-center space-x-3 mb-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={isWorking}
                      onChange={() => handleDayToggle(day.key)}
                      disabled={disabled}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 mx-3 border-2 rounded flex items-center justify-center transition-colors ${
                      isWorking 
                        ? 'bg-black border-black' 
                        : 'bg-white border-gray-300 hover:border-gray-400'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      {isWorking && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-lg font-medium text-gray-900">{day.label}</span>
                </label>
              </div>

              {/* Time ranges */}
              {isWorking && (
                <div className="space-y-3">
                  {dayData.timeRanges.map((range, index) => (
                    <div key={index} className="flex items-center space-x-3">
                    {/* End time */}
                    <div className="relative">
                        <input
                            type="time"
                            value={range.end}
                            onChange={(e) => handleTimeRangeChange(day.key, index, 'end', e.target.value)}
                            disabled={disabled}
                            className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <Clock className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>

                    {/* Separator */}
                    <span className="text-gray-500 font-medium">-</span>

                    {/* Start time */}
                    <div className="relative">
                        <input
                            type="time"
                            value={range.start}
                            onChange={(e) => handleTimeRangeChange(day.key, index, 'start', e.target.value)}
                            disabled={disabled}
                            className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <Clock className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                      

                      {/* Remove button */}
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() => handleRemoveTimeRange(day.key, index)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}

                  {/* Add time range button */}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleAddTimeRange(day.key)}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm font-medium">{t.addTimeRange}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}